import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MessageSquare, Video, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';
import { track } from '../utils/firebase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      track.forgotPassword();
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MessageSquare className="w-10 h-10 text-[#4338CA]" />
            <Video className="w-10 h-10 text-[#4338CA]" />
          </div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2 font-heading">CallChat</h1>
          <p className="text-[#475569]">Connect, Chat, and Collaborate</p>
        </div>

        <div className="bg-white rounded-md border border-[#E2E8F0] p-8">
          {!sent ? (
            <>
              <h2 className="text-2xl font-bold mb-2 text-[#0F172A] font-heading">Forgot Password</h2>
              <p className="text-[#475569] text-sm mb-6">Enter your email and we'll send you a link to reset your password.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" data-testid="forgot-email-label">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-md"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-[#EEF2FF] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#4338CA]" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#0F172A] font-heading">Check Your Email</h2>
              <p className="text-[#475569] text-sm mb-6">
                We've sent a password reset link to <strong>{email}</strong>. Please check your inbox and spam folder.
              </p>
              <Button
                onClick={() => setSent(false)}
                variant="outline"
                className="w-full border-[#E2E8F0]"
                data-testid="forgot-resend-button"
              >
                Resend Email
              </Button>
            </div>
          )}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-[#4338CA] hover:underline inline-flex items-center gap-1 text-sm" data-testid="back-to-login-link">
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
